import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { selectionProcessesAdmin } from '@/services/selectionProcesses';
import { Loader } from '@/components/custom/loader';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel, FieldDescription } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { SelectNative } from '@/components/ui/select-native';
import { Switch } from '@/components/ui/switch';

const KIND_OPTIONS = [
  { value: 'short_text', label: 'Texto curto' },
  { value: 'long_text', label: 'Texto longo' },
  { value: 'radio', label: 'Múltipla escolha (uma resposta)' },
  { value: 'select', label: 'Seleção em lista' },
  { value: 'checkbox', label: 'Marcação múltipla' },
  { value: 'file', label: 'Anexo de arquivo' }
];

const CHOICE_KINDS = ['radio', 'select', 'checkbox'];

const DEFAULT_FIELDS = [
  { label: 'Nome completo', type: 'texto' },
  { label: 'CPF', type: 'texto' },
  { label: 'Data de nascimento', type: 'data' },
  { label: 'Email', type: 'texto' },
  { label: 'Telefone', type: 'texto' },
  { label: 'Regional', type: 'seleção' },
  { label: 'Unidade', type: 'seleção' },
  { label: 'Escolaridade atual', type: 'seleção', studentOnly: true },
  { label: 'Série pretendida', type: 'seleção', studentOnly: true },
  { label: 'Turno preferido', type: 'seleção', studentOnly: true },
  { label: 'Necessidades especiais', type: 'sim/não' },
  { label: 'Descrição das necessidades', type: 'texto longo' },
  { label: 'Aceite LGPD', type: 'consentimento' }
];

function emptyDraft() {
  return { id: null, label: '', kind: 'short_text', required: false, options: ['', ''] };
}

function normalizeAttrs(item) {
  const a = item.attributes || item;
  return {
    id: item.id,
    label: a.label,
    kind: a.kind,
    required: !!a.required,
    position: a.position ?? 0,
    options: Array.isArray(a.options) ? a.options : []
  };
}

export default function StepQuestions({ processId, targetAudience = 'student', collectEducationInfo = true, onBack, onNext, onCountChange }) {
  const visibleDefaultFields = useMemo(() => DEFAULT_FIELDS.filter(f => !f.studentOnly || (targetAudience === 'student' && collectEducationInfo)), [targetAudience, collectEducationInfo]);
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [draft, setDraft] = useState(null);
  const [submittingDraft, setSubmittingDraft] = useState(false);
  const [defaultsOpen, setDefaultsOpen] = useState(false);

  const fetchQuestions = async () => {
    if (!processId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await selectionProcessesAdmin.questions.list(processId);
      const items = (Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : []).map(normalizeAttrs);
      const sorted = items.sort((a, b) => (a.position ?? 0) - (b.position ?? 0) || a.id - b.id);
      setQuestions(sorted);
      if (onCountChange) onCountChange(sorted.length);
    } catch {
      toast.error('Erro ao carregar perguntas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processId]);

  const isChoice = useMemo(() => draft && CHOICE_KINDS.includes(draft.kind), [draft]);

  const startCreate = () => setDraft(emptyDraft());

  const startEdit = q => {
    setDraft({
      id: q.id,
      label: q.label,
      kind: q.kind,
      required: q.required,
      options: Array.isArray(q.options) && q.options.length > 0 ? [...q.options] : ['', '']
    });
  };

  const handleSaveDraft = async () => {
    if (!processId) {
      toast.error('Salve o seletivo antes de adicionar perguntas.');
      return;
    }
    if (!draft.label.trim() || draft.label.trim().length < 2) {
      toast.error('A pergunta deve ter ao menos 2 caracteres.');
      return;
    }
    if (CHOICE_KINDS.includes(draft.kind)) {
      const opts = draft.options.map(o => o.trim()).filter(Boolean);
      if (opts.length < 2) {
        toast.error('Adicione ao menos 2 opções.');
        return;
      }
      if (new Set(opts).size !== opts.length) {
        toast.error('Opções não podem se repetir.');
        return;
      }
    }

    setSubmittingDraft(true);
    try {
      const payload = {
        label: draft.label.trim(),
        kind: draft.kind,
        required: draft.required,
        position: draft.id ? undefined : questions.length,
        options: CHOICE_KINDS.includes(draft.kind) ? draft.options.map(o => o.trim()).filter(Boolean) : []
      };
      if (draft.id) {
        await selectionProcessesAdmin.questions.update(processId, draft.id, payload);
        toast.success('Pergunta atualizada.');
      } else {
        await selectionProcessesAdmin.questions.create(processId, payload);
        toast.success('Pergunta criada.');
      }
      setDraft(null);
      await fetchQuestions();
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      const message = Array.isArray(apiErrors) ? apiErrors.map(e => e.detail || e.message || JSON.stringify(e)).join('; ') : err?.message || 'Erro ao salvar pergunta.';
      toast.error(message);
    } finally {
      setSubmittingDraft(false);
    }
  };

  const handleRemove = async q => {
    if (!window.confirm(`Remover a pergunta "${q.label}"?`)) return;
    try {
      await selectionProcessesAdmin.questions.destroy(processId, q.id);
      toast.success('Pergunta removida.');
      await fetchQuestions();
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      const message = Array.isArray(apiErrors) ? apiErrors.map(e => e.detail || e.message || JSON.stringify(e)).join('; ') : err?.message || 'Erro ao remover.';
      toast.error(message);
    }
  };

  const handleDragEnd = async result => {
    if (!result.destination) return;
    const fromIdx = result.source.index;
    const toIdx = result.destination.index;
    if (fromIdx === toIdx) return;

    const reordered = [...questions];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    setQuestions(reordered);

    try {
      const items = reordered.map((qq, i) => ({ id: qq.id, position: i }));
      await selectionProcessesAdmin.questions.reorder(processId, items);
    } catch {
      toast.error('Erro ao reordenar.');
      fetchQuestions();
    }
  };

  if (!processId) {
    return (
      <div className="page page--wrap">
        <div className="page__col">
          <p className="text-sm text-neutral">Salve o seletivo antes de adicionar perguntas.</p>
          <div className="mt-4 flex gap-3">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onBack}
            >
              Voltar
            </Button>
            <Button
              type="button"
              variant="default"
              size="md"
              onClick={onNext}
            >
              Próximo
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <Loader />;

  return (
    <div className="page page--wrap">
      <div className="page__col">
        <div className="page__content mb-12 animate-fade-in">
          <div className="new-questions__header">
            <h2 className="new-questions__title">Perguntas adicionais</h2>
            <p className="text-sm text-gray-500">Adicione perguntas extras ao formulário público. Os campos padrão são sempre coletados.</p>
          </div>

          <div className="mt-6 rounded-md border border-gray-200 bg-gray-50">
            <button
              type="button"
              className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold text-gray-700"
              onClick={() => setDefaultsOpen(o => !o)}
            >
              <span>Campos padrão (sempre coletados) — {visibleDefaultFields.length}</span>
              <span className="text-xs text-gray-500">{defaultsOpen ? 'esconder' : 'mostrar'}</span>
            </button>
            {defaultsOpen && (
              <ul className="border-t border-gray-200 px-4 py-3 text-xs text-gray-600">
                {visibleDefaultFields.map(({ label, type }) => (
                  <li
                    key={label}
                    className="flex items-center justify-between border-b border-dashed border-gray-200 py-1.5 last:border-0"
                  >
                    <span>{label}</span>
                    <span className="text-gray-400">{type}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-6 flex flex-col gap-3">
            {questions.length === 0 && !draft && <p className="text-sm text-gray-500">Nenhuma pergunta adicional ainda.</p>}

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="selection-process-questions">
                {dropProvided => (
                  <div
                    ref={dropProvided.innerRef}
                    {...dropProvided.droppableProps}
                    className="flex flex-col gap-3"
                  >
                    {questions.map((q, idx) => (
                      <Draggable
                        key={q.id}
                        draggableId={String(q.id)}
                        index={idx}
                      >
                        {(dragProvided, snapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            className={`flex items-start gap-3 rounded-md border bg-white p-4 ${snapshot.isDragging ? 'border-blue-300 shadow-lg' : 'border-gray-200'}`}
                          >
                            <div
                              {...dragProvided.dragHandleProps}
                              aria-label="Arrastar para reordenar"
                              className="flex cursor-grab select-none items-center text-gray-400 hover:text-gray-600 active:cursor-grabbing"
                              title="Arrastar para reordenar"
                            >
                              <svg
                                width="14"
                                height="20"
                                viewBox="0 0 14 20"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <circle cx="3" cy="3" r="1.5" />
                                <circle cx="3" cy="10" r="1.5" />
                                <circle cx="3" cy="17" r="1.5" />
                                <circle cx="11" cy="3" r="1.5" />
                                <circle cx="11" cy="10" r="1.5" />
                                <circle cx="11" cy="17" r="1.5" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-gray-800">{q.label}</span>
                                {q.required && <span className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-600">Obrigatória</span>}
                                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600">{KIND_OPTIONS.find(k => k.value === q.kind)?.label || q.kind}</span>
                              </div>
                              {Array.isArray(q.options) && q.options.length > 0 && <p className="mt-1 text-xs text-gray-500">{q.options.join(' · ')}</p>}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="xs"
                                onClick={() => startEdit(q)}
                              >
                                Editar
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="xs"
                                onClick={() => handleRemove(q)}
                              >
                                Remover
                              </Button>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {dropProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {draft && (
              <div className="border-primary-200 bg-primary-50 rounded-md border p-4">
                <h3 className="mb-3 text-sm font-semibold text-gray-800">{draft.id ? 'Editar pergunta' : 'Nova pergunta'}</h3>

                <div className="flex flex-col gap-4">
                  <Field>
                    <FieldLabel htmlFor="draft-label">* Pergunta</FieldLabel>
                    <Input
                      id="draft-label"
                      value={draft.label}
                      onChange={e => setDraft({ ...draft, label: e.target.value })}
                      placeholder="Ex: Você possui acesso à internet em casa?"
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="draft-kind">* Tipo</FieldLabel>
                    <SelectNative
                      id="draft-kind"
                      value={draft.kind}
                      onChange={e => setDraft({ ...draft, kind: e.target.value })}
                    >
                      {KIND_OPTIONS.map(opt => (
                        <option
                          key={opt.value}
                          value={opt.value}
                        >
                          {opt.label}
                        </option>
                      ))}
                    </SelectNative>
                  </Field>

                  <div className="flex items-center justify-between gap-3">
                    <FieldLabel htmlFor="draft-required">Obrigatória</FieldLabel>
                    <Switch
                      id="draft-required"
                      checked={draft.required}
                      onCheckedChange={checked => setDraft({ ...draft, required: checked })}
                    />
                  </div>

                  {draft.kind === 'file' && <FieldDescription>O participante poderá enviar 1 arquivo (PDF, JPG ou PNG, até 5 MB).</FieldDescription>}

                  {isChoice && (
                    <Field>
                      <FieldLabel>Opções (mínimo 2)</FieldLabel>
                      <FieldDescription>Uma opção por linha; remover uma linha apaga a opção.</FieldDescription>
                      <div className="mt-2 flex flex-col gap-2">
                        {draft.options.map((opt, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-2"
                          >
                            <Input
                              value={opt}
                              onChange={e => {
                                const next = [...draft.options];
                                next[i] = e.target.value;
                                setDraft({ ...draft, options: next });
                              }}
                              placeholder={`Opção ${i + 1}`}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="xs"
                              onClick={() => {
                                const next = draft.options.filter((_, j) => j !== i);
                                setDraft({ ...draft, options: next.length === 0 ? [''] : next });
                              }}
                              disabled={draft.options.length <= 2}
                            >
                              ×
                            </Button>
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="xs"
                          onClick={() => setDraft({ ...draft, options: [...draft.options, ''] })}
                        >
                          + Adicionar opção
                        </Button>
                      </div>
                    </Field>
                  )}

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="default"
                      size="md"
                      onClick={handleSaveDraft}
                      disabled={submittingDraft}
                    >
                      {submittingDraft ? 'Salvando...' : 'Salvar pergunta'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="md"
                      onClick={() => setDraft(null)}
                      disabled={submittingDraft}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {!draft && (
              <Button
                type="button"
                variant="outline"
                size="md"
                onClick={startCreate}
              >
                + Adicionar pergunta
              </Button>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            size="md"
            onClick={onBack}
          >
            Voltar
          </Button>
          <Button
            type="button"
            variant="default"
            size="md"
            wide
            onClick={onNext}
          >
            Próximo
          </Button>
        </div>
      </div>
    </div>
  );
}
